import { DeleteOutline, Edit, VisibilityTwoTone } from "@mui/icons-material";
import { Link, TextField, Tooltip } from "@mui/material";
import { useState } from "react";
import { isExecutiveManager } from "../../lib/userUtils";
import { getComparator, stableSort } from "../../lib/utils";
import TableComponent from "../../ui/data/table/TableComponent";
import ActionMenu from "../../ui/primitives/ActionMenu";
import AttachmentModal from "./AttachmentModal";

const AttachmentTable = ({
  data,
  headerTabs,
  isLoading,
  editableFields,
  handleDeleteEntity,
  handleUpdateEntity,
  defaultSort,
  order,
  setOrder,
  isEditable = true,
}) => {
  const [editingRowId, setEditingRowId]   = useState(null);
  const [editingValues, setEditingValues] = useState({});
  const [orderBy, setOrderBy]             = useState(defaultSort || "");

  const [showAttachment, setShowAttachment]   = useState(false);
  const [defaultSelected, setDefaultSelected] = useState(0);

  const handleEditClick = (row) => {
    setTimeout(() => {
      setEditingRowId(row.id);
      setEditingValues({ ...row });
    }, 0);
  };

  const handleEditClose = () => {
    setEditingRowId(null);
    setEditingValues({});
  };

  const handleBlurSave = async (id) => {
    const trimmed = editingValues.file_name?.trim();
    if (trimmed) {
      await handleUpdateEntity(id, { ...editingValues, file_name: trimmed });
    }
    setEditingRowId(null);
    setEditingValues({});
  };

  const handleRowInputChange = (e, key) => {
    const { value } = e.target;
    setEditingValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleOpen  = (id) => { setShowAttachment(true); setDefaultSelected(id); };
  const handleClose = () => { setShowAttachment(false); setDefaultSelected(null); };

  const handleSort = (key) => {
    setOrder(order === "asc" ? "desc" : "asc");
    setOrderBy(key);
  };

  const sortedData = stableSort(data, getComparator(order, orderBy));

  const buildHeaders = () =>
    headerTabs.map((col) => ({
      ...col,
      formatContent: (row) => {
        const isEditing = editingRowId === row.id;

        if (editableFields?.includes(col.key) && isEditing) {
          return (
            <TextField
              autoFocus
              size="small"
              value={editingValues[col.key] ?? row[col.key] ?? ""}
              onChange={(e) => handleRowInputChange(e, col.key)}
              onBlur={() => handleBlurSave(row.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.target.blur();
                if (e.key === "Escape") handleEditClose();
              }}
              variant="outlined"
              sx={{ minWidth: 140 }}
            />
          );
        }
        if (col.key === "file_name") {
          const rowIndex = sortedData.findIndex((r) => r.id === row.id);
          const isImage  = row.content_type?.includes("image");

          const nameLabel = row.file_name || "N/A";

          if (isImage) {
            return (
              <Tooltip title="View attachment">
                <Link
                  component="button"
                  underline="hover"
                  onClick={() => handleOpen(rowIndex)}
                  sx={{ color: "primary.main", cursor: "pointer", fontWeight: 500 }}
                >
                  {nameLabel}
                </Link>
              </Tooltip>
            );
          }

          return (
            <Tooltip title="Open file">
              <Link
                href={row.url}
                target="_blank"
                rel="noreferrer"
                underline="hover"
                sx={{ color: "primary.main", fontWeight: 500 }}
              >
                {nameLabel}
              </Link>
            </Tooltip>
          );
        }
        if (col.formatContent) return col.formatContent(row);
        return row[col.key] || "N/A";
      },
    }));

  const renderActions = (row) => {
    if (row.gainss_project_id === null) return null;

    const rowIndex = sortedData.findIndex((r) => r.id === row.id);
    const isImage  = row.content_type?.includes("image");
    const canEdit  = isEditable && !isExecutiveManager();

    const menuItems = [
      {
        label: isImage ? "View attachment" : "Open file",
        icon: <VisibilityTwoTone />,
        onClick: isImage
          ? () => handleOpen(rowIndex)
          : () => window.open(row.url, "_blank", "noreferrer"),
      },

      canEdit && {
        label: "Edit name",
        icon: <Edit />,
        onClick: () => handleEditClick(row),
      },
      canEdit && handleDeleteEntity && {
        label: "Delete",
        icon: <DeleteOutline />,
        onClick: () => handleDeleteEntity(row),
        color: "error",
      },
    ].filter(Boolean);

    return <ActionMenu items={menuItems} tooltip="Attachment actions" />;
  };

  return (
    <>
      <TableComponent
        data={sortedData}
        headerTabs={buildHeaders()}
        isLoading={isLoading}
        action={renderActions}
        tablekey="attachments"
        order={order}
        setOrder={setOrder}
        defaultSort={defaultSort}
        serverSideSorting={false}
        onSortChange={(key) => handleSort(key)}
        showPages={false}
        showCheckbox={false}
        showSearch={false}
        showFilter={false}
      />

      <AttachmentModal
        show={showAttachment}
        handleClose={handleClose}
        attachments={sortedData}
        defaultSelected={defaultSelected}
        setDefaultSelected={setDefaultSelected}
      />
    </>
  );
};

export default AttachmentTable;